import { useState } from 'react';

import type { ListHomeCareVendors } from '@virtual-care/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Label,
  Textarea,
} from '@virtual-care/shadcn';
import { useAction } from '@virtual-care/ui';

export type HomeCareVendor = ListHomeCareVendors['records'][number];

interface SuspensionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorId: string;
  vendorName: string;
}

export function SuspensionDialog({
  open,
  onOpenChange,
  vendorId,
  vendorName,
}: SuspensionDialogProps) {
  const [suspensionReason, setSuspensionReason] = useState('');

  const suspendMutation = useAction('POST /homecare/{id}/suspend', {
    invalidate: ['GET /homecare'],
    onSuccess: () => {
      onOpenChange(false);
      setSuspensionReason('');
    },
  });

  const handleConfirm = () => {
    if (suspensionReason.length >= 10) {
      suspendMutation.mutate({ id: vendorId, reason: suspensionReason });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="rtl:text-right">
            Suspend Vendor
          </AlertDialogTitle>
          <AlertDialogDescription className="rtl:text-right">
            Please provide a reason for suspending {vendorName}. This reason
            will be sent to the vendor.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="reason">Suspension Reason</Label>
            <Textarea
              id="reason"
              placeholder="Enter the reason for suspension (minimum 10 characters)"
              value={suspensionReason}
              onChange={(e) => setSuspensionReason(e.target.value)}
              rows={4}
            />
            {suspensionReason.length > 0 && suspensionReason.length < 10 && (
              <p className="text-sm text-red-500">
                Reason must be at least 10 characters
              </p>
            )}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={suspensionReason.length < 10 || suspendMutation.isPending}
            className="bg-amber-600 text-white hover:bg-amber-700"
          >
            {suspendMutation.isPending ? 'Suspending...' : 'Suspend Vendor'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
